// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { PDFParse } from 'pdf-parse';
import turndownBrowserEs from 'turndown/lib/turndown.browser.es';
PDFParse.setWorker('/pdf-parse/pdf.worker.min.js');

import { derived, writable } from 'svelte/store';
import { getDocumentStoreConfig } from '../config.js';
import docxWorkerUrl from '../workers/docxParser.worker.js';
import { parseDocxInWorker } from './workerManager.js';

const PARSE_TIMEOUT_MS = 10000; // unified timeout for all parsers (10 seconds)

// MAX n Documents & Tokens

// 400 Tokens entsprechen ca. einer DIN-A4 Seite.
const MAX_DOCUMENTS = getDocumentStoreConfig().max_documents;

// Maximum number of Tokens over all uploaded documents
const MAX_TOTAL_TOKENS = getDocumentStoreConfig().max_total_tokens;
const APPROX_CHARS_PER_TOKEN = 4.5; // basierend auf 1800 Zeichen ≈ 400 Tokens
const MAX_TOTAL_CHARS = Math.round(MAX_TOTAL_TOKENS * APPROX_CHARS_PER_TOKEN);

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt'];

// Helper function to format file size
function formatFileSize(bytes) {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(2)} KB`;
}

// Helper function to check if file type is valid
function isValidFileType(file) {
  const fileName = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext.toLowerCase()));
}

//store used for 'only documents'-settings in recherche page
const documents = writable({});

const hasShownLimitWarning = writable(false);

// Derived store to track document count, char usage and limit status
export const documentStats = derived(documents, ($documents) => {
  const n_docs = Object.keys($documents).length;
  const totalChars = Object.values($documents).reduce((sum, doc) => {
    if (!doc?.status || doc.status === 'parsed') {
      return sum + (doc?.n_chars || 0);
    }
    return sum;
  }, 0);
  const activeChars = Object.values($documents).reduce(
    (sum, doc) => sum + (doc?.is_active ? doc?.n_chars || 0 : 0),
    0,
  );
  const totalTokens = Math.round(totalChars / APPROX_CHARS_PER_TOKEN);
  const activeTokens = Math.round(activeChars / APPROX_CHARS_PER_TOKEN);
  const isDocLimitReached = n_docs >= MAX_DOCUMENTS;
  const remainingDocSlots = Math.max(MAX_DOCUMENTS - n_docs, 0);
  const remainingChars = Math.max(MAX_TOTAL_CHARS - activeChars, 0);
  const remainingTokens = Math.max(MAX_TOTAL_TOKENS - activeTokens, 0);
  const charUsagePercent =
    MAX_TOTAL_CHARS > 0
      ? Math.min((activeChars / MAX_TOTAL_CHARS) * 100, 100)
      : 0;
  const isCharLimitExceeded = activeChars > MAX_TOTAL_CHARS;

  return {
    count: n_docs,
    isDocLimitReached,
    maxCount: MAX_DOCUMENTS,
    remainingDocSlots,
    totalChars,
    activeChars,
    remainingChars,
    totalTokens,
    activeTokens,
    remainingTokens,
    charLimit: MAX_TOTAL_CHARS,
    tokenLimit: MAX_TOTAL_TOKENS,
    charUsagePercent,
    isCharLimitExceeded,
  };
});

async function addFiles(files) {
  /*
    Adds a new File to the Document Store.
    1. Calculate a SHA256 as key to avoid duplication
    2. Add document to store in "preparing"-state
    3. Call parser && update document to "parsing"-state
    4. Update document in store with the parsed text or with an error state
     */

  const incomingFiles = Array.isArray(files) ? files : Array.from(files);
  const summary = {
    attempted: incomingFiles.length,
    added: 0,
    duplicates: [],
    limitReached: false,
    deactivatedDueToLimit: [],
    tooLarge: [],
    failed: [],
  };

  // Get current document state and check limits
  let currentCount = 0;
  documents.update((docs) => {
    currentCount = Object.keys(docs).length;
    return docs;
  });

  // Check if limit is already reached
  const isLimitReached = currentCount >= MAX_DOCUMENTS;

  if (isLimitReached) {
    summary.limitReached = true;
    console.warn(
      `Limit reached: ${currentCount} documents. Maximum ${MAX_DOCUMENTS} documents allowed.`,
    );
    return summary;
  }

  // Calculate how many documents can still be added
  const remainingDocSlots = Math.max(MAX_DOCUMENTS - currentCount, 0);

  if (remainingDocSlots <= 0) {
    summary.limitReached = true;
    return summary;
  }

  const filesToAdd = incomingFiles.slice(0, remainingDocSlots).flat(Infinity);

  if (filesToAdd.length === 0) {
    if (incomingFiles.length > 0) {
      summary.limitReached = true;
    }
    console.warn(`Maximum number of documents (${MAX_DOCUMENTS}) reached.`);
    return summary;
  }

  if (filesToAdd.length < incomingFiles.length) {
    console.warn(
      `Only ${filesToAdd.length} of ${incomingFiles.length} files added. Maximum ${MAX_DOCUMENTS} documents allowed.`,
    );
  }

  const parsePromises = [];

  // iterate over FileList and add files to documents
  for (const file of filesToAdd) {
    // Check file type before processing
    if (!isValidFileType(file)) {
      console.warn(
        `File ${file.name} has invalid file type. Only PDF, DOCX and TXT are allowed. Skipping.`,
      );
      continue;
    }

    const hash = await hashFileSHA256(file);

    // check if document is in store and continue if true
    let documentExists = false;
    documents.update((docs) => {
      documentExists = docs[hash];
      return docs;
    });

    if (documentExists) {
      summary.duplicates.push(file.name);
      continue;
    }

    // document to store
    const doc = {
      id: hash,
      file: file,
      text: '',
      n_chars: 0,
      is_active: false,
      isTooLarge: false,
      status: 'preparing',
    };

    documents.update((docs) => ({
      ...docs,
      [hash]: doc,
    }));

    parsePromises.push(
      parseFileToDocument(doc)
        .then((parsed_doc) => applyParsedDocument(parsed_doc))
        .catch((err) => {
          console.error(
            'Die folgende Datei konnte nicht verarbeitet werden. Datei: ',
            doc.file.name,
            'Error: ',
            err.toString(),
          );
          setDocumentStatus(doc, 'error');
          return {
            name: doc.file.name,
            error: true,
          };
        }),
    );
  }

  if (parsePromises.length > 0) {
    const results = await Promise.all(parsePromises);
    results.forEach((result) => {
      if (!result) return;
      if (result.error) {
        summary.failed.push(result.name);
        return;
      }
      summary.added += 1;
      if (result.tooLarge) {
        summary.tooLarge.push(result.name);
        return;
      }
      if (result.deactivatedDueToLimit) {
        summary.deactivatedDueToLimit.push(result.name);
      }
    });
  }

  return summary;
}

function setDocumentStatus(doc, status) {
  /*
    Sets the `status`-field of a document in the document store.
     */
  documents.update((docs) => ({
    ...docs,
    [doc.id]: { ...doc, status: status },
  }));
}

function setDocumentActiveState(id, active_state) {
  /*
    Sets the `is_active` field in a document while respecting the char limit.
     */
  const result = { changed: false, reason: null, isActive: active_state };
  documents.update((docs) => {
    const current_doc = docs[id];
    if (!current_doc) {
      console.warn('Das Dokument konnte nicht gefunden werden. ID:', id);
      return docs;
    }

    if (active_state) {
      const otherActiveChars = calculateActiveChars(docs, id);
      const desiredChars =
        otherActiveChars + (current_doc.n_chars ? current_doc.n_chars : 0);
      if (desiredChars > MAX_TOTAL_CHARS) {
        result.reason =
          (current_doc.n_chars || 0) > MAX_TOTAL_CHARS
            ? 'doc-too-large'
            : 'char-limit';
        result.isActive = current_doc.is_active;
        return docs;
      }
    }

    const updatedDoc = { ...current_doc, is_active: active_state };
    result.changed = true;
    result.isActive = active_state;
    return {
      ...docs,
      [id]: updatedDoc,
    };
  });
  return result;
}

function toggleActiveState(doc) {
  if (!doc) {
    return { changed: false, reason: 'not-found', isActive: false };
  }
  return setDocumentActiveState(doc.id, !doc.is_active);
}

function deleteFile(doc) {
  documents.update((docs) => {
    try {
      delete docs[doc.id];
    } catch (e) {
      console.error('Could not delete document', doc.file.name, e);
    }

    if (Object.keys(docs).length < MAX_DOCUMENTS) {
      hasShownLimitWarning.set(false);
    }

    return docs;
  });
}
// Empties the document store (for example used when resetting the chat)
function resetDocuments() {
  documents.set({});
  hasShownLimitWarning.set(false);
}

function calculateActiveChars(docs, excludeId = null) {
  return Object.values(docs)
    .filter(
      (doc) =>
        doc?.is_active &&
        doc?.status === 'parsed' &&
        (excludeId == null || doc?.id !== excludeId),
    )
    .reduce((sum, doc) => sum + (doc.n_chars ? doc.n_chars : 0), 0);
}

function applyParsedDocument(parsedDocument) {
  const result = {
    name: parsedDocument?.file?.name || '',
    deactivatedDueToLimit: false,
    tooLarge: false,
    error: false,
  };

  documents.update((docs) => {
    const currentDocs = { ...docs };
    const existingDoc = currentDocs[parsedDocument.id] || {};
    const activeCharsWithoutCurrent = calculateActiveChars(
      currentDocs,
      parsedDocument.id,
    );
    const currentChars = parsedDocument.n_chars || 0;
    const desiredChars = activeCharsWithoutCurrent + currentChars;
    const docTooLarge = currentChars > MAX_TOTAL_CHARS;
    const canActivate = !docTooLarge && desiredChars <= MAX_TOTAL_CHARS;

    const finalDoc = {
      ...existingDoc,
      ...parsedDocument,
      is_active: canActivate,
      isTooLarge: docTooLarge,
    };

    if (docTooLarge) {
      result.tooLarge = true;
    } else if (!canActivate) {
      result.deactivatedDueToLimit = true;
    }

    currentDocs[parsedDocument.id] = finalDoc;
    return currentDocs;
  });

  return result;
}

// Helper functions
function bufferToHex(buffer) {
  /*
    Helper: ArrayBuffer -> hex string
     */
  const bytes = new Uint8Array(buffer);
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex;
}

async function hashFileSHA256(file) {
  /*
    Calculates the SHA256 hash value of a file.
     */
  const ab = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', ab);
  return bufferToHex(hashBuffer);
}

// Abortable TXT parser helper
async function readTextWithAbort(file, timeoutMs) {
  let reader;
  let timeoutId;
  return await new Promise((resolve, reject) => {
    let done = false;
    const finalize = (fn, arg) => {
      if (done) return;
      done = true;
      clearTimeout(timeoutId);
      if (reader) {
        reader.onload = null;
        reader.onerror = null;
      }
      fn(arg);
    };

    reader = new FileReader();
    reader.onload = (e) => finalize(resolve, e.target.result);
    reader.onerror = (e) => finalize(reject, e);

    timeoutId = setTimeout(() => {
      try {
        reader.abort();
      } catch (_) {}
      finalize(reject, new Error('Datei-Lesen abgebrochen (Timeout)'));
    }, timeoutMs);

    try {
      reader.readAsText(file);
    } catch (e) {
      finalize(reject, e);
    }
  });
}

// Promise race with timeout; cancellation via cancelFlag
function withTimeout(promise, timeoutMs, errorMsg, cancelFlag = null) {
  const startTime = Date.now();

  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      if (cancelFlag) {
        cancelFlag.cancelled = true; // Signal to parser to stop
      }
      // Do not show warning message for now since no impact
      // console.warn(`Timeout reached after ${timeoutMs}ms`);
      reject(new Error(errorMsg));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    const elapsed = Date.now() - startTime;
  });
}

async function parsePdf(file, cancelFlag = { cancelled: false }) {
  console.debug(`PDF parsing started: ${file.name}`);
  const arrayBuffer = await file.arrayBuffer();

  if (cancelFlag.cancelled) {
    console.error('PDF parsing cancelled after arrayBuffer load');
    throw new Error('Parsing cancelled');
  }

  const parser = new PDFParse({ data: arrayBuffer });
  try {
    const text = await parser.getText();

    if (cancelFlag.cancelled) {
      console.error('PDF parsing cancelled after getText');
      throw new Error('Parsing cancelled');
    }

    console.debug(`PDF parsing completed: ${text.length} characters`);
    return text;
  } finally {
    await parser.destroy();
  }
}

// Reading docx as arrayBuffer and sent to worker for abordable/terminatable parsing
async function parseDocx(file, timeoutMs) {
  console.debug(`DOCX parsing in worker: ${file.name}`);

  const arrayBuffer = await file.arrayBuffer();
  const { html, stats } = await parseDocxInWorker(
    docxWorkerUrl,
    arrayBuffer,
    timeoutMs,
  );

  // Convert HTML -> Markdown on main thread in small chunks to keep UI responsive
  const turndownService = new turndownBrowserEs();
  const markdown = await convertHtmlToMarkdownChunked(html, turndownService);

  console.debug(`DOCX parsing completed: ${markdown.length} characters`);
  return markdown;
}

/*C onvert HTML to Markdown in small chunks to keep UI responsive
 * Compared to the DOCX to HTML part this is the not so heavy part
 * If it would have been a too big file it would not have passed in the worker before
 */

async function convertHtmlToMarkdownChunked(
  html,
  turndownService,
  chunkSize = 5000,
) {
  if (!html || html.length === 0) return '';
  if (html.length <= chunkSize) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(turndownService.turndown(html));
        } catch (e) {
          reject(e);
        }
      }, 0);
    });
  }

  const chunks = [];
  for (let i = 0; i < html.length; i += chunkSize) {
    chunks.push(html.slice(i, i + chunkSize));
  }
  const parts = [];
  for (let i = 0; i < chunks.length; i++) {
    const part = await new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(turndownService.turndown(chunks[i]));
        } catch (e) {
          reject(e);
        }
      }, 0);
    });
    parts.push(part);
    // small yield every few chunks
    if (i % 10 === 0) await new Promise((r) => setTimeout(r, 1));
  }
  return parts.join('\n');
}

async function parseFileToDocument(document) {
  /*
   **Parses different file formats in there different ways
   *PDF: Use PDF-Parse library with timeout
   *DOCX: Use Web Worker with true abort capability (and helper classes workerManager.js and docxParser.worker.js)
   *TXT: Use FileReader with timeout
   *Other file types: not allowed
   */

  setDocumentStatus(document, 'parsing');

  let text = '';
  let parsedChars = 0;
  const type = document.file.type;

  if (
    type === 'application/pdf' ||
    document.file.name?.toLowerCase().endsWith('.pdf')
  ) {
    const cancelFlag = { cancelled: false };
    const pdfPayload = await withTimeout(
      parsePdf(document.file, cancelFlag),
      PARSE_TIMEOUT_MS,
      'PDF-Parsing abgebrochen (Timeout)',
      cancelFlag,
    );
    if (typeof pdfPayload === 'string') {
      text = pdfPayload;
      parsedChars = pdfPayload.length;
    } else if (pdfPayload && typeof pdfPayload === 'object') {
      text = pdfPayload.text || '';
      parsedChars = Number.isFinite(pdfPayload.chars)
        ? pdfPayload.chars
        : (pdfPayload.text || '').length;
    } else {
      text = '';
      parsedChars = 0;
    }

    if (!Number.isFinite(parsedChars) || parsedChars < 0) {
      parsedChars = text.length;
    }
  } else if (
    type ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    // DOCX: Use Web Worker with true abort capability
    text = await parseDocx(document.file, PARSE_TIMEOUT_MS);
    parsedChars = text.length;
  } else if (
    type.split('/')[0] === 'text' ||
    document.file.name?.toLowerCase().endsWith('.txt')
  ) {
    text = await readTextWithAbort(document.file, PARSE_TIMEOUT_MS);
    parsedChars = text.length;
  } else {
    throw new Error('Datei-Typ wird nicht unterstützt.');
  }

  return {
    ...document,
    text: text,
    n_chars: parsedChars,
    status: 'parsed',
    is_active: true,
  };
}

export default {
  documents: documents,
  addFiles: addFiles,
  deleteFile: deleteFile,
  toggleActive: toggleActiveState,
  resetDocuments: resetDocuments,
  documentStats: documentStats,
  formatFileSize: formatFileSize,
  hasShownLimitWarning: hasShownLimitWarning,
  MAX_TOTAL_TOKENS: MAX_TOTAL_TOKENS,
  APPROX_CHARS_PER_TOKEN: APPROX_CHARS_PER_TOKEN,
  MAX_TOTAL_CHARS: MAX_TOTAL_CHARS,
  MAX_DOCUMENTS: MAX_DOCUMENTS,
};
